import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BlueprintBackground } from "@/components/BlueprintBackground";
import { Logo } from "@/components/Logo";
import { Wordmark } from "@/components/Wordmark";
import { api } from "@/api/client";
import { toast } from "sonner";

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [status, setStatus] = useState("checking");
  const [resendEmail, setResendEmail] = useState("");
  const [resending, setResending] = useState(false);
  const verifiedOnceRef = useRef(false);

  useEffect(() => {
    if (!token) {
      setStatus("error");
      return;
    }
    if (verifiedOnceRef.current) return;
    verifiedOnceRef.current = true;
    api
      .post("/auth/verify-email", { token, origin_url: window.location.origin })
      .then(() => setStatus("verified"))
      .catch(() => setStatus("error"));
  }, [token]);

  const handleResend = async (e) => {
    e.preventDefault();
    if (!resendEmail.trim()) return;
    setResending(true);
    try {
      await api.post("/auth/resend-verification", { email: resendEmail.trim(), origin_url: window.location.origin });
      toast.success("If that email needs verifying, we've sent a new link.");
    } catch (e) {
      toast.error("Could not send a new verification link.");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" data-testid="verify-email-page">
      <BlueprintBackground />
      <div className="w-full max-w-sm">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <Logo size={26} />
          <Wordmark className="font-heading text-xl font-bold tracking-tight text-white" />
        </Link>
        <div className="rounded-2xl border border-slate-700/60 bg-slate-900/80 backdrop-blur-md p-8 shadow-2xl text-center">
          {status === "checking" && (
            <>
              <Loader2 className="animate-spin text-blue-400 mx-auto mb-4" size={32} />
              <p className="text-slate-300" data-testid="verify-email-checking">Verifying your email...</p>
            </>
          )}
          {status === "verified" && (
            <>
              <CheckCircle2 className="text-emerald-400 mx-auto mb-4" size={32} />
              <h1 className="font-heading text-xl font-bold text-white mb-2">Email verified</h1>
              <p className="text-slate-400 text-sm mb-6">Your account is active. You can log in now.</p>
              <Link to="/login" data-testid="verify-email-login-link">
                <Button className="w-full bg-blue-600 hover:bg-blue-700">Go to Log In</Button>
              </Link>
            </>
          )}
          {status === "error" && (
            <>
              <XCircle className="text-rose-400 mx-auto mb-4" size={32} />
              <h1 className="font-heading text-xl font-bold text-white mb-2">Link invalid or expired</h1>
              <p className="text-slate-400 text-sm mb-4">Enter your email to get a new verification link.</p>
              <form onSubmit={handleResend} className="flex gap-2">
                <Input
                  type="email"
                  required
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  placeholder="you@company.com"
                  data-testid="verify-email-resend-input"
                  className="bg-slate-800/60 border-slate-700 text-white placeholder:text-slate-500"
                />
                <Button type="submit" disabled={resending} data-testid="verify-email-resend-button" className="bg-blue-600 hover:bg-blue-700 shrink-0">
                  {resending ? <Loader2 size={14} className="animate-spin" /> : "Send"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
